import React, { type MouseEventHandler, type ReactNode } from 'react';
import { Tooltip, Menu } from '@mui/material';
import { Icon, IconButton, Loader } from '@sm-dashboard/ui-components';

/** Fixed mobile filter toolbar height — slightly taller than icon buttons (34px) to avoid open/close jump. */
export const MOBILE_FILTER_ROW_HEIGHT = 44;

type FilterToolbarIconButtonProps = {
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  'aria-label': string;
  'aria-controls'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-expanded'?: boolean | 'true' | 'false';
  children: ReactNode;
};

/** Shared filter-toolbar control. Exported for `MobilePrimaryFilterRow`. */
export const StyledFilterIconButton = ({ children, ...props }: FilterToolbarIconButtonProps) => (
  <IconButton color="primary" size="medium" shape="circle" {...props}>
    {children}
  </IconButton>
);

export type ExportButtonProps = {
  exportConfig: any;
  onExcelClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  openExcel: boolean;
  exportDisabled: boolean;
  loading: boolean;
  anchorEl: HTMLElement | null;
  exportMenuItems: any;
  onMenuItemClick: (onClick: () => void) => () => void;
  onCloseMenu: () => void;
};

export const ResetFiltersButton = ({
  hasActiveFilters,
  onReset
}: {
  hasActiveFilters: boolean;
  onReset: () => void;
}) => {
  if (!hasActiveFilters) return null;

  return (
    <Tooltip title="איפוס כל הסינונים">
      <StyledFilterIconButton aria-label="איפוס כל הסינונים" onClick={onReset}>
        <Icon icon="filterAltOffOutlined" color="inherit" />
      </StyledFilterIconButton>
    </Tooltip>
  );
};

export const ExpandFiltersButton = ({
  regularFiltersLength,
  expanded,
  onToggle
}: {
  regularFiltersLength: number;
  expanded: boolean;
  onToggle: () => void;
}) => {
  if (regularFiltersLength === 0) return null;

  const label = expanded ? 'צמצם סינון' : 'הרחב סינון';

  return (
    <Tooltip title={label}>
      <StyledFilterIconButton aria-label={label} onClick={onToggle}>
        <Icon icon={expanded ? 'filterListOff' : 'filterList'} color="inherit" />
      </StyledFilterIconButton>
    </Tooltip>
  );
};

export const ExportButton = ({
  exportConfig,
  onExcelClick,
  openExcel,
  exportDisabled,
  loading,
  anchorEl,
  exportMenuItems,
  onMenuItemClick,
  onCloseMenu
}: ExportButtonProps) => {
  if (!exportConfig?.exportToExcel && !exportConfig?.sendByEmail) return null;

  return (
    <>
      {exportConfig.sendByEmail ? (
        <Tooltip title="שלח לי במייל. עדיף לסנן לפני השליחה.">
          <StyledFilterIconButton
            onClick={() => {
              void exportConfig.sendByEmail?.();
            }}
            aria-label="שלח לי במייל"
            disabled={exportDisabled}
          >
            {loading ? <Loader size={18} color="inherit" /> : <Icon icon="mailOutline" color="inherit" />}
          </StyledFilterIconButton>
        </Tooltip>
      ) : null}
      {exportConfig.exportToExcel ? (
        <StyledFilterIconButton
          onClick={onExcelClick}
          aria-label="ייצוא"
          aria-controls={openExcel ? 'basic-menu' : undefined}
          aria-haspopup="menu"
          aria-expanded={openExcel ? 'true' : undefined}
          disabled={exportDisabled}
        >
          {loading ? <Loader size={18} color="inherit" /> : <Icon icon="fileDownloadOutlined" color="inherit" />}
        </StyledFilterIconButton>
      ) : null}
      {exportMenuItems && (
        <Menu
          anchorEl={anchorEl}
          open={openExcel}
          onClose={onCloseMenu}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
        >
          {exportMenuItems.map((child: any) =>
            React.isValidElement<any>(child)
              ? React.cloneElement<any>(child, {
                  onClick: onMenuItemClick(child.props.onClick)
                })
              : child
          )}
        </Menu>
      )}
    </>
  );
};
